/**
 * Question Attempt History — NOT Mastery
 *
 * This module tracks individual question attempt history ONLY.
 * It does NOT calculate mastery — that's handled by familyMasteryEngine.
 *
 * Purpose:
 * - Track question-level attempt counts and history
 * - Provide question status for UI feedback (learning/practiced/mastered)
 * - Support group-scoped attempt tracking
 *
 * IMPORTANT: Question status is for UI feedback only, NOT for completion logic.
 * Module completion is determined by family mastery, not question status.
 */

import { type ModuleId, getModuleQuestions } from '../content'
import { QuestionState } from './types'
import { GroupId } from '../config/systemConfig'

// ============================================================
// Configuration
// ============================================================

const ATTEMPT_CONFIG = {
  /** Consecutive correct answers needed for 'mastered' status (UI feedback) */
  CONSECUTIVE_FOR_MASTERED: 3,
  /** Consecutive correct answers needed for 'practiced' status (UI feedback) */
  CONSECUTIVE_FOR_PRACTICED: 2,
  /** Minimum total attempts before mastered status is possible */
  MIN_ATTEMPTS_FOR_MASTERED: 3,
  /** How many recent attempts to consider for status calculation */
  HISTORY_WINDOW: 8,
  /** Mastery points awarded per consecutive correct answer */
  POINTS_PER_CORRECT: 0.35,
  /** Mastery points lost per wrong answer */
  POINTS_PER_WRONG: -0.25,
  /** Minimum mastery points for 'mastered' status (UI feedback) */
  MASTERY_POINT_THRESHOLD: 0.85,
  /** Minimum mastery points for 'practiced' status (UI feedback) */
  PRACTICED_POINT_THRESHOLD: 0.5,
  /** §6.2: Maximum age (ms) for attempt history before LRU eviction */
  MAX_AGE_MS: 90 * 24 * 60 * 60 * 1000, // 90 days
}

// ============================================================
// Group-Scoped Key Helper
// ============================================================

/**
 * Create group-scoped question state key.
 * This ensures attempt history is isolated per group.
 */
export function getQuestionStateKey(questionId: string, group: number): string {
  return `${questionId}_g${group}`
}

// ============================================================
// Status Calculation Helpers (UI Feedback Only)
// ============================================================

function getConsecutiveCorrectFromEnd(
  history: {
    isCorrect: boolean
    timestamp: number
  }[],
): number {
  let count = 0
  for (let i = history.length - 1; i >= 0; i--) {
    if (history[i].isCorrect) {
      count++
    } else {
      break
    }
  }
  return count
}
function calculateMasteryPoints(
  history: {
    isCorrect: boolean
    timestamp: number
  }[],
): number {
  if (history.length === 0) return 0
  const window = history.slice(-ATTEMPT_CONFIG.HISTORY_WINDOW)
  let points = 0
  window.forEach((attempt, index) => {
    const recencyWeight = (index + 1) / window.length
    if (attempt.isCorrect) {
      points += ATTEMPT_CONFIG.POINTS_PER_CORRECT * recencyWeight
    } else {
      points += ATTEMPT_CONFIG.POINTS_PER_WRONG * recencyWeight
    }
  })
  return Math.max(0, Math.min(1, points))
}
function determineStatus(
  history: {
    isCorrect: boolean
    timestamp: number
  }[],
  masteryPoints: number,
  totalAttempts: number,
): 'unseen' | 'learning' | 'practiced' | 'mastered' {
  if (totalAttempts === 0) return 'unseen'
  const consecutiveCorrect = getConsecutiveCorrectFromEnd(history)
  if (
    consecutiveCorrect >= ATTEMPT_CONFIG.CONSECUTIVE_FOR_MASTERED &&
    totalAttempts >= ATTEMPT_CONFIG.MIN_ATTEMPTS_FOR_MASTERED &&
    masteryPoints >= ATTEMPT_CONFIG.MASTERY_POINT_THRESHOLD
  ) {
    return 'mastered'
  }
  if (
    consecutiveCorrect >= ATTEMPT_CONFIG.CONSECUTIVE_FOR_PRACTICED &&
    masteryPoints >= ATTEMPT_CONFIG.PRACTICED_POINT_THRESHOLD
  ) {
    return 'practiced'
  }
  return 'learning'
}

// ============================================================
// Core Update Function
// ============================================================

/**
 * Update question attempt history.
 * Returns updated QuestionState with new attempt recorded.
 *
 * NOTE: This does NOT calculate mastery. Status is for UI feedback only.
 */
export function updateQuestionState(
  currentState: QuestionState | undefined,
  questionId: string,
  isCorrect: boolean,
  responseTimeMs?: number,
): QuestionState {
  const now = Date.now()
  if (!currentState) {
    currentState = {
      questionId,
      status: 'unseen',
      attempts: 0,
      correctAttempts: 0,
      lastAttemptDate: 0,
      masteryPoints: 0,
      history: [],
    }
  }
  const newHistory = [
    ...currentState.history,
    {
      isCorrect,
      timestamp: now,
      responseTimeMs,
    },
  ].slice(-ATTEMPT_CONFIG.HISTORY_WINDOW)
  const newAttempts = currentState.attempts + 1
  const newCorrectAttempts = currentState.correctAttempts + (isCorrect ? 1 : 0)
  const masteryPoints = calculateMasteryPoints(newHistory)
  const newStatus = determineStatus(newHistory, masteryPoints, newAttempts)
  return {
    questionId,
    status: newStatus,
    attempts: newAttempts,
    correctAttempts: newCorrectAttempts,
    lastAttemptDate: now,
    masteryPoints,
    history: newHistory,
  }
}

// ============================================================
// Query Functions (Group-Scoped)
// ============================================================

/**
 * Get list of answered question IDs for a module in a specific group.
 */
export function getAnsweredQuestionsForGroup(
  moduleId: ModuleId,
  questionStates: Record<string, QuestionState>,
  group: GroupId,
): string[] {
  const moduleQuestions = getModuleQuestions(moduleId).filter(
    (q) => q.group === group,
  )
  return moduleQuestions
    .filter((q) => {
      const stateKey = getQuestionStateKey(q.id, group)
      const state = questionStates[stateKey]
      return state && state.attempts > 0
    })
    .map((q) => q.id)
}

/**
 * Get recent questions for a module in a specific group.
 * Returns questions sorted by most recent attempt first.
 */
export function getRecentQuestionsForGroup(
  moduleId: ModuleId,
  questionStates: Record<string, QuestionState>,
  group: GroupId,
  limit: number = 10,
): string[] {
  const moduleQuestions = getModuleQuestions(moduleId).filter(
    (q) => q.group === group,
  )
  const questionsWithTimestamps = moduleQuestions
    .map((q) => {
      const stateKey = getQuestionStateKey(q.id, group)
      const state = questionStates[stateKey]
      return {
        questionId: q.id,
        lastAttemptDate: state?.lastAttemptDate || 0,
      }
    })
    .filter((item) => item.lastAttemptDate > 0)
    .sort((a, b) => b.lastAttemptDate - a.lastAttemptDate)
  return questionsWithTimestamps.slice(0, limit).map((item) => item.questionId)
}

/**
 * Get questions needing review (status = 'learning') for a module in a specific group.
 */
export function getReviewQueueForGroup(
  moduleId: ModuleId,
  questionStates: Record<string, QuestionState>,
  group: GroupId,
): string[] {
  const moduleQuestions = getModuleQuestions(moduleId).filter(
    (q) => q.group === group,
  )
  return moduleQuestions
    .filter((q) => {
      const stateKey = getQuestionStateKey(q.id, group)
      const state = questionStates[stateKey]
      return state?.status === 'learning'
    })
    .map((q) => q.id)
}

/**
 * Get count of questions needing review for a module in a specific group.
 */
export function getReviewNeededCountForGroup(
  moduleId: ModuleId,
  questionStates: Record<string, QuestionState>,
  group: GroupId,
): number {
  return getReviewQueueForGroup(moduleId, questionStates, group).length
}

/**
 * Get questions by status for a module (group-scoped).
 * NOTE: Status is for UI feedback only, NOT for completion logic.
 */
export function getQuestionsByStatus(
  moduleId: ModuleId,
  status: 'unseen' | 'learning' | 'practiced' | 'mastered',
  questionStates: Record<string, QuestionState>,
  group?: GroupId,
): string[] {
  let moduleQuestions = getModuleQuestions(moduleId)
  if (group !== undefined) {
    moduleQuestions = moduleQuestions.filter((q) => q.group === group)
  }
  return moduleQuestions
    .filter((q) => {
      const stateKey =
        group !== undefined ? getQuestionStateKey(q.id, group) : q.id
      const state = questionStates[stateKey]
      if (status === 'unseen') {
        return !state || state.status === 'unseen'
      }
      return state?.status === status
    })
    .map((q) => q.id)
}

/**
 * Get module statistics (group-scoped).
 * NOTE: These stats are for UI display only, NOT for completion logic.
 */
export function getModuleStats(
  moduleId: ModuleId,
  questionStates: Record<string, QuestionState>,
  group?: GroupId,
) {
  let moduleQuestions = getModuleQuestions(moduleId)
  if (group !== undefined) {
    moduleQuestions = moduleQuestions.filter((q) => q.group === group)
  }
  const stats = {
    total: moduleQuestions.length,
    unseen: 0,
    learning: 0,
    practiced: 0,
    mastered: 0,
    reviewNeeded: 0,
  }
  moduleQuestions.forEach((q) => {
    const stateKey =
      group !== undefined ? getQuestionStateKey(q.id, group) : q.id
    const state = questionStates[stateKey]
    if (!state || state.status === 'unseen') {
      stats.unseen++
    } else if (state.status === 'mastered') {
      stats.mastered++
    } else if (state.status === 'practiced') {
      stats.practiced++
    } else if (state.status === 'learning') {
      stats.learning++
      stats.reviewNeeded++
    }
  })
  return stats
}

// ============================================================
// §6.2 Storage Quota: LRU Eviction
// ============================================================

export interface EvictionReport {
  /** Number of entries before eviction */
  totalBefore: number
  /** Number of entries evicted */
  evicted: number
  /** Number of entries remaining */
  totalAfter: number
  /** Oldest remaining entry timestamp */
  oldestRemaining: number | null
  /** Cutoff date used */
  cutoffDate: number
}

/**
 * Evict stale question attempt history entries.
 */
export function evictStaleAttempts(
  questionStates: Record<string, QuestionState>,
  maxAgeMs: number = ATTEMPT_CONFIG.MAX_AGE_MS,
): { cleaned: Record<string, QuestionState>; report: EvictionReport } {
  const cutoff = Date.now() - maxAgeMs
  const cleaned: Record<string, QuestionState> = {}
  let evicted = 0
  let oldestRemaining: number | null = null

  for (const [key, state] of Object.entries(questionStates)) {
    if (state.lastAttemptDate < cutoff && state.lastAttemptDate > 0) {
      evicted++
    } else {
      cleaned[key] = state
      if (
        state.lastAttemptDate > 0 &&
        (oldestRemaining === null || state.lastAttemptDate < oldestRemaining)
      ) {
        oldestRemaining = state.lastAttemptDate
      }
    }
  }

  const totalBefore = Object.keys(questionStates).length

  return {
    cleaned,
    report: {
      totalBefore,
      evicted,
      totalAfter: totalBefore - evicted,
      oldestRemaining,
      cutoffDate: cutoff,
    },
  }
}

/**
 * Get the total size of the attempt history for diagnostics.
 */
export function getAttemptHistorySize(
  questionStates: Record<string, QuestionState>,
): {
  totalEntries: number
  totalAttempts: number
  oldestEntry: number | null
  newestEntry: number | null
} {
  const entries = Object.values(questionStates)
  let totalAttempts = 0
  let oldest: number | null = null
  let newest: number | null = null

  for (const state of entries) {
    totalAttempts += state.attempts
    if (state.lastAttemptDate > 0) {
      if (oldest === null || state.lastAttemptDate < oldest) {
        oldest = state.lastAttemptDate
      }
      if (newest === null || state.lastAttemptDate > newest) {
        newest = state.lastAttemptDate
      }
    }
  }

  return {
    totalEntries: entries.length,
    totalAttempts,
    oldestEntry: oldest,
    newestEntry: newest,
  }
}
